import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { AudioClip, AudioRole, Project } from "../../../engine/src/types.ts";
import { ApiError, type AssetInfo, type AssetList, type BuiltinSound, type TranscriptCueInfo, type TtsVoice, type TtsVoices } from "../lib/api.ts";
import { addClip, envelope, removeClip, scheduleAt, splitClip, updateClip, waveformSlice } from "../lib/audio.ts";
import { applyCues, cuesFromTranscript } from "../lib/subtitles.ts";
import { getLang, t, type Key } from "../lib/i18n.ts";
import type { EditorStore } from "../lib/store.ts";
import { Err, Spinner, msgOf, useApp } from "./common.tsx";

export interface SoundMeta { id: string; name: string; duration_ms: number; peaks: number[]; builtin: boolean; role: AudioRole; transcript?: TranscriptCueInfo[] | null }
export interface AudioCtxValue { list: AssetList | null; meta: (id: string) => SoundMeta | null; refresh: () => Promise<void>; player: AudioPlayer; muted: boolean; setMuted: (m: boolean) => void }
export const AudioCtx = createContext(null as unknown as AudioCtxValue);
export const useAudio = (): AudioCtxValue => useContext(AudioCtx);

const builtinName = (b: BuiltinSound) => b.name[getLang() as "fr" | "en"] ?? b.name.en;
export function metaFrom(list: AssetList | null, id: string): SoundMeta | null {
  const b = list?.builtin.find((x) => x.id === id);
  if (b) return { id, name: builtinName(b), duration_ms: b.duration_ms, peaks: b.peaks, builtin: true, role: b.kind };
  const a = list?.assets.find((x) => x.id === id);
  return a ? { id, name: a.name, duration_ms: a.duration_ms, peaks: a.peaks, builtin: false, role: a.kind === "voice" ? "voice" : "sfx", transcript: a.transcript } : null;
}

/** WebAudio preview player, synchronised with the editor's single clock. Everything audible in the editor goes
 *  through here; the exported video is mixed separately on the server with the same clip parameters. */
function envAt(env: [number, number][], x: number): number {
  if (!env.length) return 1;
  if (x <= env[0]![0]) return env[0]![1];
  for (let i = 1; i < env.length; i++) {
    const [x1, v1] = env[i]!, [x0, v0] = env[i - 1]!;
    if (x <= x1) return x1 === x0 ? v1 : v0 + ((v1 - v0) * (x - x0)) / (x1 - x0);
  }
  return env[env.length - 1]![1];
}

export class AudioPlayer {
  private ctx: AudioContext | null = null;
  private buffers = new Map<string, Promise<AudioBuffer | null>>();
  private live: AudioBufferSourceNode[] = [];
  private master: GainNode | null = null;
  private t0ctx = 0; private t0ms = 0; private token = 0;
  playing = false; muted = false; failed = new Set<string>();
  onFail: ((id: string) => void) | null = null;
  private urlOf: (id: string) => string;
  constructor(urlOf: (id: string) => string) { this.urlOf = urlOf; }

  private ensure(): AudioContext {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain(); this.master.gain.value = this.muted ? 0 : 1; this.master.connect(this.ctx.destination);
    }
    return this.ctx;
  }
  setMuted(m: boolean): void { this.muted = m; if (this.master) this.master.gain.value = m ? 0 : 1; }
  private load(id: string): Promise<AudioBuffer | null> {
    let p = this.buffers.get(id);
    if (!p) {
      p = fetch(this.urlOf(id), { credentials: "same-origin" }).then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.arrayBuffer(); })
        .then((b) => this.ensure().decodeAudioData(b)).catch(() => { this.failed.add(id); this.onFail?.(id); return null; });
      this.buffers.set(id, p);
    }
    return p;
  }
  forget(id: string): void { this.buffers.delete(id); }
  /** Begin (or restart after a seek / edit) from the time returned by getTimeMs() at the moment sound can actually start. */
  async start(project: Project, getTimeMs: () => number): Promise<void> {
    const clips = project.audio;
    this.stopSources(); const my = ++this.token; this.playing = true;
    const ctx = this.ensure(); void ctx.resume();
    const ids = [...new Set(clips.filter((c) => !c.muted && c.volume > 0).map((c) => c.asset_id))];
    const bufs = new Map<string, AudioBuffer | null>(await Promise.all(ids.map(async (id) => [id, await this.load(id)] as const)));
    if (my !== this.token) return;                                   // superseded by a newer start/stop while decoding
    const now = getTimeMs(), base = ctx.currentTime + 0.03;
    this.t0ctx = base; this.t0ms = now;
    for (const pb of scheduleAt(clips, now)) {
      const buf = bufs.get(pb.clip.asset_id); if (!buf) continue;
      const src = ctx.createBufferSource(); src.buffer = buf;
      const g = ctx.createGain(), c = pb.clip, into = pb.offsetMs - c.in_ms, T = base + pb.delayMs / 1000;
      const env = envelope(project, c);                               // volume x fades x ducking, in clip-local ms
      g.gain.setValueAtTime(envAt(env, into), T);
      for (const [x, v] of env) if (x > into) g.gain.linearRampToValueAtTime(v, T + (x - into) / 1000);
      src.connect(g); g.connect(this.master!);
      src.start(T, Math.min(pb.offsetMs / 1000, buf.duration), Math.min(pb.playMs / 1000, Math.max(0, buf.duration - pb.offsetMs / 1000)));
      this.live.push(src);
    }
  }
  private stopSources(): void { for (const s of this.live) { try { s.stop(); } catch { /* already stopped */ } s.disconnect(); } this.live = []; }
  stop(): void { this.token++; this.playing = false; this.stopSources(); }
  /** Where the audio clock thinks the project time is (for drift detection after a seek). */
  expectedMs(): number { return this.ctx ? this.t0ms + (this.ctx.currentTime - this.t0ctx) * 1000 : 0; }
  dispose(): void { this.stop(); void this.ctx?.close(); this.ctx = null; }
}

export function useAudioState(): AudioCtxValue {
  const { api } = useApp();
  const [list, setList] = useState(null as AssetList | null);
  const [muted, setMutedS] = useState(false);
  const player = useMemo(() => new AudioPlayer((id: string) => api.assetUrl(id)), []);
  const refresh = async () => { try { setList(await api.listAssets()); } catch { /* keep the previous list */ } };
  useEffect(() => { void refresh(); return () => player.dispose(); }, []);
  const setMuted = (m: boolean) => { setMutedS(m); player.setMuted(m); };
  return { list, meta: (id: string) => metaFrom(list, id), refresh, player, muted, setMuted };
}

/** Wires the player to the store: play/pause, restarts on clip edits, and on seeks while playing. */
export function usePlaybackSync(store: EditorStore, A: AudioCtxValue, project: Project, playing: boolean, timeMs: number): void {
  const { player } = A;
  const getTime = () => store.getState().timeMs;
  useEffect(() => { if (playing) void player.start(project, getTime); else player.stop(); }, [playing, project.audio, project.ducking]);
  useEffect(() => { if (playing && Math.abs(timeMs - player.expectedMs()) > 200) void player.start(project, getTime); }, [timeMs, playing]);
}

export function Waveform({ meta, clip, width, height }: { meta: SoundMeta | null; clip: AudioClip; width: number; height: number }) {
  if (!meta) return null;
  const cols = Math.max(1, Math.min(Math.floor(width / 2), 600));
  const bars = waveformSlice(meta.peaks, meta.duration_ms, clip.in_ms, clip.duration_ms, cols);
  const mid = height / 2, step = width / bars.length;
  const d = bars.map((v, i) => { const h = Math.max(1, v * (height - 4)); return `M${(i * step + step / 2).toFixed(1)} ${(mid - h / 2).toFixed(1)}v${h.toFixed(1)}`; }).join("");
  return <svg width={width} height={height} style={{ display: "block", pointerEvents: "none" }} aria-hidden="true"><path d={d} stroke="currentColor" strokeWidth={Math.max(1, step * 0.7)} opacity={clip.muted ? 0.25 : 0.7} /></svg>;
}
const fmtMB = (b: number) => (b / 1048576 >= 10 ? Math.round(b / 1048576) : Math.round(b / 104857.6) / 10) + " MB";
const fmtS = (ms: number) => (ms / 1000).toFixed(1) + " s";

export function AudioLibrary({ store }: { store: EditorStore }) {
  const { api } = useApp();
  const A = useAudio();
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(null as string | null);
  const [previewing, setPreviewing] = useState(null as string | null);
  const el = useRef(null as HTMLAudioElement | null);
  const file = useRef(null as HTMLInputElement | null);
  useEffect(() => () => { el.current?.pause(); }, []);

  async function upload(f: File | undefined) {
    if (!f) return;
    setBusy(true); setErr(null);
    try { await api.uploadAsset(f); await A.refresh(); } catch (e) { setErr(msgOf(e)); } finally { setBusy(false); if (file.current) file.current.value = ""; }
  }
  function preview(id: string) {
    el.current?.pause();
    if (previewing === id) { setPreviewing(null); return; }
    const a = new Audio(api.assetUrl(id)); el.current = a; setPreviewing(id);
    a.onended = () => setPreviewing(null); a.onerror = () => { setPreviewing(null); setErr(t("au.decode_fail")); };
    void a.play().catch(() => setPreviewing(null));
  }
  function add(m: SoundMeta) {
    const s = store.getState(), r = addClip(s.project!, m, s.timeMs, m.role);
    if (!r) { setErr(t("au.no_room")); return; }
    setErr(null); store.edit(() => r.project); store.select({ kind: "audio", id: r.clipId });
  }
  async function del(m: SoundMeta) {
    setErr(null);
    try { await api.deleteAsset(m.id); A.player.forget(m.id); await A.refresh(); } catch (e) { setErr(e instanceof ApiError && e.code === "asset_in_use" ? t("err.asset_in_use") + " " + e.message.replace(/^.*: /, "") : msgOf(e)); }
  }
  const mine = (A.list?.assets ?? []).filter((a: AssetInfo) => a.kind !== "image" && a.kind !== "video").map((a: AssetInfo) => metaFrom(A.list, a.id)!);
  const builtin = (A.list?.builtin ?? []).map((b: BuiltinSound) => metaFrom(A.list, b.id)!);
  const st = A.list?.storage;
  const row = (m: SoundMeta) => (
    <div className="snd" key={m.id}>
      <button className="btn sm icon" onClick={() => preview(m.id)} aria-label={(previewing === m.id ? t("au.stop") : t("au.preview")) + " " + m.name} aria-pressed={previewing === m.id}>{previewing === m.id ? "■" : "▶"}</button>
      <div className="snd-name"><span title={m.name}>{m.name}</span><small>{fmtS(m.duration_ms)}</small></div>
      <button className="btn sm icon" onClick={() => add(m)} aria-label={t("au.add") + ": " + m.name} title={t("au.add")}>＋</button>
      {!m.builtin && <button className="btn sm icon danger" onClick={() => void del(m)} aria-label={t("au.delete_file") + ": " + m.name} title={t("au.delete_file")}>✕</button>}
    </div>);
  return (
    <div className="stack" aria-label={t("au.title")}>
      <h3 className="h">{t("au.title")}</h3>
      <input ref={file} type="file" className="sr" id="audio-file" accept="audio/*,.mp3,.wav,.ogg,.flac,.m4a,.aac" onChange={(e: any) => void upload(e.target.files?.[0])} />
      <label htmlFor="audio-file" className="btn sm" style={{ cursor: busy ? "wait" : "pointer" }} aria-disabled={busy}>{busy ? <><Spinner /> {t("au.uploading")}</> : t("au.upload")}</label>
      <Err>{err}</Err>
      <VoicePanel store={store} />
      {st && <div><div className="bar" aria-hidden="true"><i style={{ width: `${Math.min(100, (st.used_bytes / st.limit_bytes) * 100)}%` }} /></div>
        <small className="muted">{t("au.storage", { used: fmtMB(st.used_bytes), max: fmtMB(st.limit_bytes) })}</small></div>}
      <div><h4 className="h">{t("au.mine")}</h4>{mine.length ? mine.map(row) : <small className="muted">{t("au.empty", { size: st ? fmtMB(st.max_upload_bytes) : "" })}</small>}</div>
      <div><h4 className="h">{t("au.builtin")}</h4>{A.list ? builtin.map(row) : <Spinner />}</div>
      <small className="muted">{t("au.privacy")}</small>
    </div>
  );
}

export function ClipInspector({ store, p, id }: { store: EditorStore; p: Project; id: string }) {
  const A = useAudio();
  const c = p.audio.find((x) => x.id === id);
  if (!c) return <p className="muted">{t("insp.none")}</p>;
  const meta = A.meta(c.asset_id), src = meta?.duration_ms ?? c.in_ms + c.duration_ms;
  const U = (patch: Partial<AudioClip>, key: string) => store.edit((q) => updateClip(q, id, patch, src), `au:${id}:${key}`);
  const N = (key: keyof AudioClip, label: Key, step = 100, scale = 1) => (
    <label className="f" htmlFor={`au-${key}`}>{t(label)}<input id={`au-${key}`} className="input" type="number" step={step} min={0}
      value={Math.round(((c[key] as number) * scale) * 100) / 100} onChange={(e: any) => { const v = parseFloat(e.target.value); if (Number.isFinite(v)) U({ [key]: v / scale } as Partial<AudioClip>, key); }} /></label>);
  return (
    <div className="stack">
      <h3 className="h">{t("au.clip")}</h3>
      <label className="f" htmlFor="au-name">{t("f.name")}<input id="au-name" className="input" maxLength={80} value={c.name} placeholder={meta?.name} onChange={(e: any) => U({ name: e.target.value }, "name")} /></label>
      <label className="f" htmlFor="au-volume">{t("au.volume")}: {Math.round(c.volume * 100)}
        <input id="au-volume" type="range" min={0} max={200} step={1} value={Math.round(c.volume * 100)} onChange={(e: any) => U({ volume: Number(e.target.value) / 100 }, "volume")} /></label>
      <div className="fields">{N("start_ms", "f.start")}{N("duration_ms", "f.duration")}{N("fade_in_ms", "au.fade_in")}{N("fade_out_ms", "au.fade_out")}<div className="wide">{N("in_ms", "au.offset")}</div></div>
      <label className="f" htmlFor="au-role">{t("au.role")}<select id="au-role" className="select" value={c.role} onChange={(e: any) => U({ role: e.target.value as AudioRole }, "role")}>
        {(["sfx", "music", "voice"] as const).map((r) => <option key={r} value={r}>{t(("role." + r) as Key)}</option>)}</select></label>
      {c.role === "voice" && meta?.transcript?.length ? <button className="btn sm" onClick={() => store.edit((q) => applyCues(q, cuesFromTranscript(q.audio.find((x) => x.id === id) ?? c, meta.transcript!)))}>{t("sub.from_voice")}</button> : null}
      <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" checked={c.muted} onChange={(e: any) => U({ muted: e.target.checked }, "muted")} />{t("au.muted")}</label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className="btn sm" onClick={() => { const r = splitClip(store.getState().project!, id, store.getState().timeMs); if (r) { store.edit(() => r.project); store.select({ kind: "audio", id: r.newId }); } }}>{t("au.split")}</button>
        <button className="btn sm danger" onClick={() => { store.edit((q) => removeClip(q, id)); store.select({ kind: "none" }); }}>{t("insp.delete")}</button>
      </div>
    </div>
  );
}

const speedKey = (v: number) => Math.round(v * 100) / 100;

/** Voice-over: free TTS runs on the server (eSpeak NG / Piper / Flite); the result is a normal audio asset with its own subtitle timings. */
export function VoicePanel({ store }: { store: EditorStore }) {
  const { api } = useApp();
  const A = useAudio();
  const [info, setInfo] = useState(null as TtsVoices | null);
  const [text, setText] = useState(""); const [voice, setVoice] = useState(""); const [speed, setSpeed] = useState(1);
  const [subs, setSubs] = useState(true); const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null as string | null); const [ok, setOk] = useState(false);
  const load = async () => { try { const v = await api.ttsVoices(); setInfo(v); setVoice((cur: string) => cur || v.voices[0]?.id || ""); } catch (e) { setErr(msgOf(e)); } };
  useEffect(() => { void load(); }, []);
  async function create() {
    setBusy(true); setErr(null); setOk(false);
    try {
      const a = await api.tts({ text, voice, speed });
      await A.refresh(); void load();
      const s = store.getState(), meta = metaFrom({ ...(A.list ?? { builtin: [], storage: { used_bytes: 0, limit_bytes: 0, max_upload_bytes: 0 } }), assets: [a] }, a.id)!;
      const r = addClip(s.project!, meta, s.timeMs, "voice");
      if (!r) { setErr(t("au.no_room")); return; }
      store.edit((q) => {
        const withClip = r.project, clip = withClip.audio.find((c) => c.id === r.clipId)!;
        return subs && a.transcript?.length ? applyCues(withClip, cuesFromTranscript(clip, a.transcript)) : withClip;
      });
      store.select({ kind: "audio", id: r.clipId }); setOk(true);
    } catch (e) { setErr(msgOf(e)); } finally { setBusy(false); }
  }
  const max = info?.max_chars ?? 600;
  return (
    <details className="stack" open>
      <summary className="h" style={{ cursor: "pointer" }}>{t("vo.title")}</summary>
      {info && info.voices.length === 0 ? <div className="alert warn" role="status">{t("vo.no_voices")}</div> : (
        <div className="stack">
          <label className="f" htmlFor="vo-text">{t("vo.text")}
            <textarea id="vo-text" className="textarea" rows={4} maxLength={max} value={text} placeholder={t("vo.placeholder")} onChange={(e: any) => setText(e.target.value)} /></label>
          <small className="muted">{t("vo.chars", { n: text.length, max })}</small>
          <label className="f" htmlFor="vo-voice">{t("vo.voice")}
            <select id="vo-voice" className="select" value={voice} onChange={(e: any) => setVoice(e.target.value)}>
              {(info?.voices ?? []).map((v: TtsVoice) => <option key={v.id} value={v.id}>{v.label}</option>)}</select></label>
          <label className="f" htmlFor="vo-speed">{t("vo.speed", { n: speedKey(speed) })}
            <input id="vo-speed" type="range" min={0.6} max={1.6} step={0.05} value={speed} onChange={(e: any) => setSpeed(Number(e.target.value))} /></label>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" checked={subs} onChange={(e: any) => setSubs(e.target.checked)} />{t("vo.subs")}</label>
          <button className="btn sm primary" disabled={busy || !text.trim() || !voice} onClick={() => void create()}>{busy ? <><Spinner /> {t("vo.creating")}</> : t("vo.create")}</button>
          <Err>{err}</Err>
          {ok && <div className="alert" role="status">{t("vo.done")}</div>}
          {info && <small className="muted">{t("vo.quota", { used: info.used_today, max: info.per_day })}</small>}
          <small className="muted">{t("vo.license")}</small>
        </div>)}
    </details>
  );
}

export function DuckingPanel({ store, p }: { store: EditorStore; p: Project }) {
  const d = p.ducking;
  const U = (patch: Partial<Project["ducking"]>, key: string) => store.edit((q) => ({ ...q, ducking: { ...q.ducking, ...patch } }), `duck:${key}`);
  return (
    <div className="stack">
      <h3 className="h">{t("du.title")}</h3>
      <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" checked={d.enabled} onChange={(e: any) => U({ enabled: e.target.checked }, "on")} />{t("du.enabled")}</label>
      <label className="f" htmlFor="du-amount">{t("du.amount", { n: d.amount_db })}
        <input id="du-amount" type="range" min={0} max={30} step={1} value={d.amount_db} disabled={!d.enabled} onChange={(e: any) => U({ amount_db: Number(e.target.value) }, "amt")} /></label>
      <div className="fields">
        <label className="f" htmlFor="du-attack">{t("du.attack")}<input id="du-attack" className="input" type="number" min={0} max={2000} step={10} value={d.attack_ms} disabled={!d.enabled}
          onChange={(e: any) => { const v = parseInt(e.target.value, 10); if (Number.isFinite(v)) U({ attack_ms: Math.min(2000, Math.max(0, v)) }, "att"); }} /></label>
        <label className="f" htmlFor="du-release">{t("du.release")}<input id="du-release" className="input" type="number" min={0} max={5000} step={10} value={d.release_ms} disabled={!d.enabled}
          onChange={(e: any) => { const v = parseInt(e.target.value, 10); if (Number.isFinite(v)) U({ release_ms: Math.min(5000, Math.max(0, v)) }, "rel"); }} /></label>
      </div>
      <small className="muted">{t("du.hint")}</small>
    </div>
  );
}


/** "A sound for this element": put one of the built-in effects exactly where the element appears (its entrance, else the scene start). */
export function ElementSound({ store, elementId }: { store: EditorStore; elementId: string }) {
  const A = useAudio();
  const [pick, setPick] = useState("bi_pop");
  const sfx = (A.list?.builtin ?? []).filter((b: BuiltinSound) => b.kind === "sfx");
  if (!sfx.length) return null;
  function put() {
    const st = store.getState(), p = st.project; if (!p) return;
    const sc = p.scenes.find((x) => x.element_ids.includes(elementId)); if (!sc) return;
    const track = p.tracks.find((tr) => tr.element_id === elementId && tr.property === "reveal") ?? p.tracks.find((tr) => tr.element_id === elementId && tr.property === "opacity");
    const at = sc.start_ms + (track && track.keyframes.length ? track.keyframes[0]!.time_ms : 0);
    const b = sfx.find((x: BuiltinSound) => x.id === pick); if (!b) return;
    const r = addClip(p, { id: b.id, name: b.name[getLang()], duration_ms: Math.min(b.duration_ms, 1200) }, at, "sfx");
    if (r) store.edit(() => r.project);
  }
  return (
    <div><h3 className="h">{t("snd.element")}</h3>
      <div className="fields">
        <label className="f wide">{t("snd.pick")}<select className="select" value={pick} onChange={(e: any) => setPick(e.target.value)}>
          {sfx.map((b: BuiltinSound) => <option key={b.id} value={b.id}>{b.name[getLang()]}</option>)}</select></label>
        <button className="btn sm wide" onClick={put}>＋ {t("snd.add_at_entrance")}</button>
      </div>
    </div>
  );
}
